import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, UserCheck } from "lucide-react";
import { JOIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  partner_type: "",
  current_role: "",
  message: "",
};

const PARTNER_TYPES = [
  {
    value: "Referral Partner",
    testId: JOIN.partnerTypeReferral,
    Icon: Users,
    label: "Referral Partner",
    caption: "I want to refer clients and earn a referral fee.",
  },
  {
    value: "Agent",
    testId: JOIN.partnerTypeAgent,
    Icon: UserCheck,
    label: "Agent",
    caption: "I want to actively sell and represent SmartEdge programs and receive residual income.",
  },
];

export default function PartnerApplicationForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.partner_type) {
      setError("Please choose how you'd like to partner with SmartEdge.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-application`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        partner_type: form.partner_type,
        current_role: form.current_role.trim() || null,
        message: form.message.trim() || null,
      });
      setSubmitted(true);
      toast.success("Application received — we'll be in touch within 2 business days.");
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email dpaul@smartedgebizsolutions.com.";
      setError(String(detail));
      toast.error("Could not submit your application.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid={JOIN.success}
        className="se-card"
        style={{ padding: 40, textAlign: "left" }}
      >
        <div className="se-badge mb-4">Application received</div>
        <h3 className="type-subheading mb-3">Thanks — we&apos;ve got it.</h3>
        <p className="type-body">
          We&apos;ll review your application and reach out within 2 business days
          to schedule an onboarding call.
        </p>
        <button
          type="button"
          className="btn-ghost mt-6"
          onClick={() => setSubmitted(false)}
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={JOIN.form}
      className="se-card"
      style={{ padding: 40 }}
      noValidate
    >
      {/* Partner type toggle */}
      <fieldset
        data-testid={JOIN.partnerType}
        className="mb-6"
        style={{ border: "none", padding: 0, margin: 0 }}
      >
        <legend
          className="se-label"
          style={{ marginBottom: 10, padding: 0 }}
        >
          I&apos;m applying as{" "}
          <span style={{ color: "#b42318" }} aria-hidden="true">*</span>
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PARTNER_TYPES.map((opt) => {
            const active = form.partner_type === opt.value;
            return (
              <label
                key={opt.value}
                htmlFor={`jt-partner-${opt.value}`}
                data-testid={opt.testId}
                className="partner-toggle"
                data-active={active ? "true" : "false"}
              >
                <input
                  type="radio"
                  id={`jt-partner-${opt.value}`}
                  name="partner_type"
                  value={opt.value}
                  checked={active}
                  onChange={onChange}
                  required
                  className="sr-only"
                />
                <span
                  className="partner-toggle__icon"
                  aria-hidden="true"
                >
                  <opt.Icon size={20} strokeWidth={1.5} />
                </span>
                <span className="flex-1">
                  <span className="partner-toggle__label">{opt.label}</span>
                  <span className="partner-toggle__caption">{opt.caption}</span>
                </span>
                <span
                  className="partner-toggle__radio"
                  aria-hidden="true"
                >
                  <span />
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="se-label" htmlFor="jt-name">Full name</label>
          <input
            id="jt-name"
            name="name"
            type="text"
            required
            className="se-input"
            data-testid={JOIN.inputName}
            value={form.name}
            onChange={onChange}
            placeholder="Jane Partner"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="jt-email">Email</label>
          <input
            id="jt-email"
            name="email"
            type="email"
            required
            className="se-input"
            data-testid={JOIN.inputEmail}
            value={form.email}
            onChange={onChange}
            placeholder="jane@company.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="jt-phone">Phone</label>
          <input
            id="jt-phone"
            name="phone"
            type="tel"
            required
            className="se-input"
            data-testid={JOIN.inputPhone}
            value={form.phone}
            onChange={onChange}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
        </div>
        <div>
          <label className="se-label" htmlFor="jt-role">
            Current role / business{" "}
            <span style={{ fontWeight: 400, color: "var(--color-body)" }}>(optional)</span>
          </label>
          <input
            id="jt-role"
            name="current_role"
            type="text"
            className="se-input"
            data-testid={JOIN.inputRole}
            value={form.current_role}
            onChange={onChange}
            placeholder="e.g. Business consultant, financial advisor…"
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="se-label" htmlFor="jt-message">
          Message{" "}
          <span style={{ fontWeight: 400, color: "var(--color-body)" }}>(optional)</span>
        </label>
        <textarea
          id="jt-message"
          name="message"
          rows={5}
          className="se-input"
          data-testid={JOIN.inputMessage}
          value={form.message}
          onChange={onChange}
          placeholder="Tell us about your experience and network..."
          style={{ resize: "vertical", minHeight: 120 }}
        />
      </div>

      {error && (
        <div
          data-testid={JOIN.error}
          className="mt-4 type-caption"
          style={{ color: "#b42318" }}
        >
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <p className="type-caption" style={{ maxWidth: 380 }}>
          Response within 2 business days · Confidential and never shared.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary btn-primary--lg"
          data-testid={JOIN.submit}
        >
          {submitting ? "Sending…" : "Submit Application"}
        </button>
      </div>
    </form>
  );
}
